import { useEffect } from "react";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import CommsThread, { type TwilioMessage } from "./CommsThread";
import type { TeamMember } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import { formatPhone } from "../../lib/phoneFormat";

export interface DrawerDeal {
  id: string;
  homeowner_name: string | null;
  homeowner_phone: string | null;
  homeowner_email?: string | null;
  claim_number?: string | null;
  /** human stage label, e.g. "Collecting $" */
  stageLabel?: string | null;
  /** the stage group's dot colour class, e.g. "bg-emerald-500" */
  stageDotClass?: string;
  /** open balance for the header (post_build_balance_remaining) */
  balance?: number | null;
}

/**
 * The reply drawer a board opens on a deal (Finance → Supplements, Oct 16, 2026):
 * a right-side panel on laptops (fixed, full height, ~440px, above the board,
 * which keeps scrolling and its filters), a bottom sheet over a backdrop on
 * phones. Header = name, stage, balance, phone, the Call button, an email link
 * and "Open deal"; body = the live comms thread in compact mode. Esc, the ✕ or
 * (on phones) the backdrop close it. Render one per board and switch `deal`.
 */
export default function DealCommsDrawer({
  deal,
  members,
  onClose,
  onSent,
}: {
  /** null = closed */
  deal: DrawerDeal | null;
  members: Record<string, TeamMember>;
  onClose: () => void;
  /** a text went out from the drawer's compose box */
  onSent?: (dealId: string, message: TwilioMessage) => void;
}) {
  const open = !!deal;

  // Esc closes. Phones get a sheet over a backdrop, so the page behind freezes;
  // laptops keep the board scrollable beside the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const isPhone = window.matchMedia("(max-width: 639px)").matches;
    const prevOverflow = document.body.style.overflow;
    if (isPhone) document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      if (isPhone) document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!deal) return null;

  const email = deal.homeowner_email?.trim() || null;
  const balance = deal.balance != null && Number.isFinite(Number(deal.balance)) ? Number(deal.balance) : null;

  return (
    <div className="fixed inset-0 z-50 sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[440px] sm:max-w-full">
      {/* Backdrop — phones only */}
      <div className="absolute inset-0 bg-slate-900/40 sm:hidden" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-label={`Texts with ${deal.homeowner_name ?? "the homeowner"}`}
        className="absolute inset-x-0 bottom-0 flex h-[88vh] flex-col rounded-t-2xl bg-slate-50 shadow-2xl sm:static sm:h-full sm:rounded-none sm:border-l sm:border-slate-200 sm:shadow-[-12px_0_32px_rgba(15,23,42,0.18)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* Header */}
        <div className="border-b border-slate-200 bg-white px-4 pb-2.5 pt-3 rounded-t-2xl sm:rounded-none">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Text thread</div>
              <div className="truncate text-base font-bold text-bardin-ink">{deal.homeowner_name ?? "(no name)"}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                {deal.stageLabel && (
                  <span className="inline-flex items-center gap-1 font-semibold text-slate-600">
                    {deal.stageDotClass && <span className={"inline-block h-2 w-2 rounded-full " + deal.stageDotClass} />}
                    {deal.stageLabel}
                  </span>
                )}
                {balance != null && (
                  <span>
                    Balance{" "}
                    <strong className={balance > 0 ? "text-red-700" : "text-emerald-700"}>{fmtUSD(balance)}</strong>
                  </span>
                )}
                {deal.claim_number && <span>#{deal.claim_number}</span>}
                {deal.homeowner_phone && <span className="tabular-nums">{formatPhone(deal.homeowner_phone)}</span>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close"
              title="Close (Esc)"
            >
              ✕
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <DealCallText dealId={deal.id} homeownerName={deal.homeowner_name} homeownerPhone={deal.homeowner_phone} callOnly />
            {email && (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
                title={email}
              >
                ✉️ Email
              </a>
            )}
            <DealLink id={deal.id} className="ml-auto text-[11px] font-bold">
              Open deal ›
            </DealLink>
          </div>
        </div>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-3 py-3">
          <CommsThread
            key={deal.id}
            dealId={deal.id}
            homeownerPhone={deal.homeowner_phone}
            members={members}
            compact
            onSent={(m) => onSent?.(deal.id, m)}
          />
        </div>
      </div>
    </div>
  );
}
